// apps/web/src/pages/Gallery/VideoFilter.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMediaPreview } from '../../../shared/contexts/MediaPreviewContext';
import SmartVideoPlayer from '../../../web/pages/Presentation/SmartVideoPlayer';
import { mixPreviewWeb } from '../../../shared/utils/mixPreviewWeb';
import {
  FILTER_PRESETS,
  type FilterPresetId,
  getWebFilterStyle,
} from '../../../shared/components/filterHelpers.web';
import { useTranslation } from 'react-i18next';

type LocState = {
  sidebarData?: any;
  mediaItem?: { id: string; file?: File; url?: string; filterId?: FilterPresetId };
  remoteUrl?: string; // optional direct video URL
};

const SIGN: Record<FilterPresetId, string> = {
  none:   '◎',

  // subtle
  soft:   '▫️',
  warm:   '🔆',
  icy:    '❄️',
  pastel: '🎨',

  // medium
  sunny:  '☀️',
  frozen: '🧊',
  dusk:   '🌇',
  noir:   '⬛',
  gold:   '🥇',
  rose:   '🌹',
  teal:   '🌊',
  amber:  '🟧',
  film:   '🎞️',

  // strongest
  cinema: '🎬',
};

// ---- helpers: URL join & checks ----
const isHttp = (u?: string | null) => !!u && /^https?:\/\//i.test(u || '');

// IMPORTANT: strip a trailing "/api" if present
const getApiOrigin = () =>
  (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001')
    .replace(/\/api\/?$/, '') // remove /api
    .replace(/\/$/, '');      // remove trailing slash

const toApiAbsolute = (u?: string | null) => {
  if (!u) return '';
  if (isHttp(u)) return u;

  // If server returns "/api/...", just prefix origin
  if (u.startsWith('/api/')) return `${getApiOrigin()}${u}`;

  // If "/something", prefix origin
  if (u.startsWith('/')) return `${getApiOrigin()}${u}`;

  // Bare "api/..." or other
  const clean = u.replace(/^\/+/, '');
  const path = clean.startsWith('api/') ? clean : `api/${clean}`;
  return `${getApiOrigin()}/${path}`;
};

const toAbsolute = (u?: string | null) => (isHttp(u) ? u! : undefined);

// ---------- component ----------

export default function VideoFilter() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation() as { state?: LocState };
  const { mediaItems, addOrReplaceMediaItem } = useMediaPreview();
  const { t } = useTranslation();

  const mediaItem = state?.mediaItem || mediaItems.find(m => m.id === id);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [httpUrl, setHttpUrl] = useState<string | null>(null);

  const initialPreset = (mediaItem as any)?.filterId as FilterPresetId | undefined;
  const [filterId, setFilterId] = useState<FilterPresetId>(initialPreset ?? 'none');

  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    let revoke: string | null = null;

    const file = (mediaItem as any)?.file as File | undefined;
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      setObjectUrl(url);
      setHttpUrl(null);
      revoke = url;
      return () => {
        if (revoke) URL.revokeObjectURL(revoke);
      };
    }

    const remote =
      toAbsolute(state?.remoteUrl) ||
      toAbsolute((mediaItem as any)?.url) ||
      null;

    setObjectUrl(null);
    setHttpUrl(remote);
    return () => {};
  }, [mediaItem, state?.remoteUrl]);

  const src = objectUrl || httpUrl || null;

  const { cssFilter, overlay } = useMemo(
    () => getWebFilterStyle(filterId),
    [filterId]
  );

  const looksGenericCameraName = (base: string) => {
    const b = base.toLowerCase();
    return (
      b === 'media-0' ||
      b === 'media-1' ||
      b === 'media' ||
      b === 'video-0' ||
      b === 'video' ||
      /^img[_-]?\d+$/i.test(base) ||
      /^vid[_-]?\d+$/i.test(base) ||
      /^pxl[_-]?\d+/i.test(base) ||
      base.length < 4
    );
  };

  const handleSave = async () => {
    if (!src || !mediaItem) return;

    setProcessing(true);
    try {
      let fileForServer: File;

      if (objectUrl) {
        const resp = await fetch(objectUrl);
        const blob = await resp.blob();
        fileForServer = new File([blob], 'video.mp4', {
          type: blob.type || 'video/mp4',
        });
      } else {
        const http = toAbsolute(src);
        if (!http) throw new Error('No usable video source found.');
        const resp = await fetch(http);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        fileForServer = new File([blob], 'video.mp4', {
          type: blob.type || 'video/mp4',
        });
      }

      const apiOrigin = getApiOrigin();

      const previewPath = await mixPreviewWeb({
        file: fileForServer,
        filterId,
        mixOriginal: true,
        apiBase: apiOrigin,
      });

      const abs = toApiAbsolute(previewPath);
      console.log('[VideoFilter] fetching processed preview from:', abs);

      const res = await fetch(abs);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Download failed ${res.status}:\n${txt.slice(0, 300)}`);
      }

      const ct = res.headers.get('content-type') || '';
      console.log('[VideoFilter] response content-type:', ct);
      if (!/video\//i.test(ct)) {
        const maybeHtml = await res.text();
        console.error(
          'Expected video/* but got:',
          ct,
          '\nBody preview:',
          maybeHtml.slice(0, 500)
        );
        throw new Error(`Server returned non-video content-type: ${ct}`);
      }

      const origBase =
        ((mediaItem as any)?.file?.name || (mediaItem as any)?.url || 'video')
          .toString()
          .split('/')
          .pop()!
          .replace(/\.[^.]+$/, '')
          .replace(/[^A-Za-z0-9._-]/g, '');

      const baseName =
        origBase && !looksGenericCameraName(origBase) ? origBase : 'video';
      const outName = `${baseName}_filtered_${Date.now()}.mp4`;

      const outBlob = await res.blob();
      const newFile = new File([outBlob], outName, { type: 'video/mp4' });

      addOrReplaceMediaItem({
        id: mediaItem.id,
        file: newFile,
        url: (mediaItem as any)?.url,
        musicTitle: (mediaItem as any)?.musicTitle ?? null,
        musicArtist: (mediaItem as any)?.musicArtist ?? null,
        filterId,
      });

      console.groupCollapsed(
        '%c[VideoFilter SAVE]',
        'color:#2aa198;font-weight:600'
      );
      console.log(
        'source chosen:',
        objectUrl ? 'objectUrl' : httpUrl ? 'httpUrl' : 'none'
      );
      console.log('filterId:', filterId);
      console.log('processed outUrl (server):', abs);
      console.log('newFile.name:', newFile.name);
      console.log('newFile.type:', newFile.type);
      console.groupEnd();

      navigate('/gallery-posting', {
        state: {
          sidebarData: state?.sidebarData,
          previewFile: newFile,
          previewFilename: newFile.name,
          editTargetId: mediaItem.id,
          isPreview: true,
        },
      });
    } catch (e) {
      console.error('Video filter save failed', e);
      alert(`❌ ${t('videoFilter.messages.processFailed')}`);
    } finally {
      setProcessing(false);
    }
  };

  const resetPreset = () => setFilterId('none');

  if (!mediaItem) {
    return <div>{t('videoFilter.messages.noMedia')}</div>;
  }

  return (
  <div style={pageShellMobile}>
    {/* VIDEO PREVIEW TOP */}
    <div style={previewCardMobile}>
      {!src ? (
        <div style={previewMessage}>
          {t('videoFilter.messages.noSource')}
        </div>
      ) : (
        <div style={previewInner}>
          <div
            style={{
              ...previewVideoShell,
              filter: cssFilter ?? 'none',
            }}
          >
            <SmartVideoPlayer
              key={`${src}|${filterId}`}
              videoUrl={src}
              controls
            />
          </div>

          {!!overlay?.tint && (overlay.opacity ?? 0) > 0 && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: overlay.tint,
                opacity: overlay.opacity,
                mixBlendMode:
                  filterId === 'noir' ? 'multiply' : 'overlay',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      )}
    </div>


    {/* HORIZONTAL FILTER SCROLLER */}
    <div style={filterScrollRow}>
      {FILTER_PRESETS.map((p) => {
        const active = filterId === p.id;
        const { overlay: sw, cssFilter: previewFilter } =
          getWebFilterStyle(p.id);

        return (
          <button
            key={p.id}
            onClick={() => setFilterId(p.id)}
            style={{
              ...filterTileMobile,
              ...(active ? filterTileActive : null),
            }}
            title={p.hint}
          >
            <div style={filterIconBubble}>
              <span style={{ fontSize: 18 }}>{SIGN[p.id]}</span>
            </div>

            <div style={filterTextCol}>
              <span style={filterName}>{p.label}</span>
            </div>

            <div style={filterPreviewChip}>
              <div
                style={{
                  ...filterPreviewChipInner,
                  filter: previewFilter ?? 'none',
                }}
              >
                {!!sw?.tint && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: sw.tint,
                      opacity: sw.opacity ?? 0,
                      mixBlendMode:
                        p.id === 'noir' ? 'multiply' : 'overlay',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>

    {/* ACTION BUTTONS */}
    <div style={mobileActions}>
      <button onClick={resetPreset} style={secondaryBtn}>
        🔄 {t('videoFilter.buttons.reset')}
      </button>

      <button
        onClick={() =>
          navigate('/gallery-posting', {
            replace: true,
            state: {
              sidebarData: state?.sidebarData ?? undefined,
              fromEditor: true,
            },
          })
        }
        style={secondaryBtn}
      >
        🔙 {t('videoFilter.buttons.back')}
      </button>

       <button
        onClick={handleSave}
        disabled={!src || processing}
        style={{
          ...primaryBtn,
          opacity: !src || processing ? 0.6 : 1,
        }}
      >
        {processing
          ? t('videoFilter.buttons.processing')
          : `📎 ${t('videoFilter.buttons.save')}`}
      </button>
    </div>
  </div>
);

}

/* ---------- styles (mirroring ImageFilter layout) ---------- */

/* Buttons – similar feel to GalleryPosting small actions */
const baseBtn: React.CSSProperties = {
   padding: '7px 8px',
  fontSize: 12,
  fontWeight: 500,
  borderRadius: 999,
  border: '1px solid #cbd5f5',
  cursor: 'pointer',
  width: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  transition:
    'background-color 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease',
};

const secondaryBtn: React.CSSProperties = {
  ...baseBtn,
  backgroundColor: '#020617',
  color: '#cbd5f5',
};

const primaryBtn: React.CSSProperties = {
  ...baseBtn,
  background: 'linear-gradient(135deg, #facc15, #fbbf24)',
  color: '#111827',
  borderColor: 'rgba(250,204,21,0.9)',
  boxShadow: '0 8px 18px rgba(250,204,21,0.35)',
};

/* Filter tiles */

const filterTileActive: React.CSSProperties = {
  borderColor: '#ffd76a',
  background:
    'linear-gradient(145deg, rgba(250, 204, 21, 0.12), rgba(15, 23, 42, 0.96))',
};

const filterIconBubble: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 999,
  background: 'radial-gradient(circle at 30% 10%, #1f2937, #020617)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const filterTextCol: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};


const filterName: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: '#e5e7eb',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};


const filterPreviewChip: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 12,
  border: '1px solid rgba(148,163,184,0.6)',
  padding: 2,
  flexShrink: 0,
  boxSizing: 'border-box',
};

const filterPreviewChipInner: React.CSSProperties = {
  width: '100%',
  height: '100%',
  borderRadius: 10,
  background: 'linear-gradient(135deg, #9ca3af, #020617)',
  position: 'relative',
  overflow: 'hidden',
};


/**
 * Slightly smaller than full, so everything fits on one page.
 * Background matches the dark editor look.
 */
const previewInner: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  maxWidth: '1200px',
  maxHeight: '100%',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  border: 'none',
  backgroundColor: 'transparent', // same family as page background
};

const previewVideoShell: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const previewMessage: React.CSSProperties = {
  fontSize: 14,
  color: '#9ca3af',
};

const pageShellMobile: React.CSSProperties = {
  height: '100vh',   
  overflow: 'hidden', 
  background: '#000',
  color: '#f9fafb',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: 10,
};

const previewCardMobile: React.CSSProperties = {
  borderRadius: 18,
  border: 'none',
  background: '#transparent',
  overflow: 'hidden',
  height: '76vh',   // 🔥 taller video
};


const filterScrollRow: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  overflowX: 'auto',
  marginTop: 4,
  paddingBottom: 4,
};

const filterTileMobile: React.CSSProperties = {
  minWidth: 110,
  height: 42,                 // 🔥 thinner
  borderRadius: 12,
  border: '1px solid rgba(148,163,184,0.35)',
  background:
    'radial-gradient(circle at top left, rgba(15,23,42,0.9), rgba(15,23,42,1))',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 8px',
  flexShrink: 0,
};

const mobileActions: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',   // 🔥 side-by-side
  gap: 8,
  marginTop: 4,
};
